import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, Building2, CheckCircle2, Award, Sparkles, TrendingUp, Camera, Truck, Sofa, PenTool, RefreshCcw, Search, X, LayoutGrid } from 'lucide-react';
import vanPhoto from '../assets/van_borbiconi.png';
import homeStagingPhoto from '../assets/home_staging.png';
import realEstatePhotosImg from '../assets/real_estate_photos.png';
import agencyConsultationImg from '../assets/agency_consultation.png';
import { AnimatePresence, motion } from 'framer-motion';
import Button from '../components/ui/Button';
import PropertyCard from '../components/PropertyCard';
import SearchHero from '../components/SearchHero';
import MapSearch from '../components/MapSearch';
import Testimonials from '../components/Testimonials';
import { AGENCY_INFO } from '../constants';
import { supabase } from '../lib/supabase';
import { useSearch } from '../context/SearchContext';


const Home = () => {
    const navigate = useNavigate();
    const [properties, setProperties] = useState([]);
    const [loading, setLoading] = useState(true);

    const {
        activeStatus, setActiveStatus,
        activeType, setActiveType,
        activeCities, setActiveCities,
        activeBudget, setActiveBudget,
        activeMinSurface, setActiveMinSurface,
        activeMinRooms, setActiveMinRooms,
        activeFeatures, setActiveFeatures,
        showFilteredResults, setShowFilteredResults,
        resetFilters,
        // Map states from global search context
        viewMode, setViewMode,
        mapApplyCityFilter
    } = useSearch();


    useEffect(() => {
        const loadProperties = async () => {
            try {
                const siteTarget = import.meta.env.VITE_SITE_TARGET || 'avantis';
                const { data, error } = await supabase
                    .from('properties')
                    .select('*')
                    .eq('site_target', siteTarget)
                    .eq(`is_published_${siteTarget}`, true)
                    .order('created_at', { ascending: false });

                if (!error && data) {
                    setProperties(data);
                } else {
                    setProperties([]);
                }
            } catch (e) {
                console.warn('[Avantis] Supabase unavailable:', e);
                setProperties([]);
            } finally {
                setLoading(false);
            }
        };

        loadProperties();
        document.title = 'Avantis Immobilier - Agence immobilière au Luxembourg';
    }, []);

    // Retourne un poids de disponibilité : les biens vendus/sous offre/compromis sont triés
    const getAvailabilityPriority = (p) => {
        const ms = (p.marketing_status || 'Disponible').toLowerCase();
        if (ms === 'vendu' || ms === 'loué' || ms.includes('compromis')) return -2000; // Trophées (bas de liste)
        if (ms.includes('offre')) return 0; // Sous offre = même priorité que disponible (le badge clignotant suffit)
        return 0; // Disponible, Nouveauté, Exclusivité, Baisse de prix
    };

    const filteredProperties = useMemo(() => {
        let candidates = properties.filter(p => {
            if (p.is_favorite) return true;
            const statusMatch = activeStatus === 'Tous' || (p.status || 'Vente') === activeStatus;
            const typeMatch = activeType === 'Tous' || p.type === activeType;
            const cityMatch = activeCities.length === 0 || activeCities.includes(p.city);
            const priceMatch = activeBudget === '' || p.price <= parseInt(activeBudget);
            return statusMatch && typeMatch && cityMatch && priceMatch;
        });

        const hasAdvancedFilters = activeMinSurface !== '' || activeMinRooms !== '' || activeFeatures.length > 0;
        if (!hasAdvancedFilters) {
            return candidates.sort((a, b) => {
                const availDiff = getAvailabilityPriority(b) - getAvailabilityPriority(a);
                if (availDiff !== 0) return availDiff;
                return (b.is_favorite ? 1 : 0) - (a.is_favorite ? 1 : 0);
            });
        }

        const scoredCandidates = candidates.map(p => {
            let score = 100;
            let missing = [];
            let sortScore = score;
            if (p.is_favorite) sortScore += 1000;
            sortScore += getAvailabilityPriority(p); // Les biens vendus/compromis tombent en bas

            if (activeMinSurface !== '' && p.surface < parseInt(activeMinSurface)) {
                score -= 20;
                sortScore -= 20;
                missing.push(`Surface < ${activeMinSurface}m²`);
            }
            const roomsCount = p.rooms || (p.features ? parseInt(p.features.find(f => f.toLowerCase().includes('chambre')) || 0) : 0);
            if (activeMinRooms !== '' && roomsCount < parseInt(activeMinRooms)) {
                score -= 20;
                sortScore -= 20;
                missing.push(`< ${activeMinRooms} Chambres`);
            }
            activeFeatures.forEach(feat => {
                let hasFeature = false;
                if (feat === 'Sans travaux') {
                    const keywords = ['rénové', 'aucun travaux', 'neuf', 'impeccable', 'refait', 'excellent état'];
                    const textContent = `${p.description} ${p.catch_phrase} ${p.features?.join(' ')}`.toLowerCase();
                    hasFeature = keywords.some(k => textContent.includes(k));
                } else {
                    hasFeature = p.features?.some(f => f.toLowerCase().includes(feat.toLowerCase()));
                }
                if (!hasFeature) {
                    score -= 10;
                    sortScore -= 10;
                    missing.push(`${feat === 'Sans travaux' ? 'Travaux à prévoir' : `Pas de ${feat}`}`);
                }
            });
            return { ...p, matchScore: Math.max(0, score), sortScore, missingCriteria: missing };
        });
        return scoredCandidates.sort((a, b) => b.sortScore - a.sortScore);
    }, [properties, activeStatus, activeType, activeCities, activeBudget, activeMinSurface, activeMinRooms, activeFeatures]);

    const mapProperties = useMemo(() => {
        return properties.filter(p => {
            if (p.is_favorite) return true;
            const statusMatch = activeStatus === 'Tous' || (p.status || 'Vente') === activeStatus;
            const typeMatch = activeType === 'Tous' || p.type === activeType;
            // Ne filtrer par activeCities sur la carte que si mapApplyCityFilter est coché
            const cityMatch = (!mapApplyCityFilter || activeCities.length === 0) || activeCities.includes(p.city);
            const priceMatch = activeBudget === '' || p.price <= parseInt(activeBudget);
            return statusMatch && typeMatch && cityMatch && priceMatch;
        });
    }, [properties, activeStatus, activeType, activeCities, activeBudget, mapApplyCityFilter]);

    const cityCounts = useMemo(() => {
        const counts = {};
        properties.forEach(p => {
            if (activeStatus === 'Tous' || (p.status || 'Vente') === activeStatus) {
                counts[p.city] = (counts[p.city] || 0) + 1;
            }
        });
        return counts;
    }, [properties, activeStatus]);

    const cities = useMemo(() => {
        return [...new Set(properties.filter(p => activeStatus === 'Tous' || (p.status || 'Vente') === activeStatus).map(p => p.city))];
    }, [properties, activeStatus]);

    const hasRentals = useMemo(() => {
        return properties.some(p => p.status === 'Location');
    }, [properties]);

    useEffect(() => {
        if (!loading && !hasRentals && (activeStatus === 'Location' || activeStatus === 'Tous')) {
            setActiveStatus('Vente');
        }
    }, [hasRentals, activeStatus, loading, setActiveStatus]);

    const isFilterActive = (hasRentals ? activeStatus !== 'Tous' : activeStatus !== 'Vente') || activeType !== 'Tous' || activeCities.length > 0 || activeBudget !== '' || activeMinSurface !== '' || activeMinRooms !== '' || activeFeatures.length > 0;

    const displayedProperties = useMemo(() => {
        if (!isFilterActive) return filteredProperties.slice(0, 6);
        return showFilteredResults ? filteredProperties : filteredProperties.slice(0, 6);
    }, [filteredProperties, isFilterActive, showFilteredResults]);

    const containerVariants = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    return (
        <>
            <SearchHero
                activeStatus={activeStatus}
                setActiveStatus={setActiveStatus}
                activeType={activeType}
                setActiveType={setActiveType}
                activeCities={activeCities}
                setActiveCities={setActiveCities}
                activeBudget={activeBudget}
                setActiveBudget={setActiveBudget}
                activeMinSurface={activeMinSurface}
                setActiveMinSurface={setActiveMinSurface}
                activeMinRooms={activeMinRooms}
                setActiveMinRooms={setActiveMinRooms}
                activeFeatures={activeFeatures}
                setActiveFeatures={setActiveFeatures}
                cities={cities}
                cityCounts={cityCounts}
                loading={loading}
                resultsCount={filteredProperties.length}
                isFilterActive={isFilterActive}
                hasRentals={hasRentals}
            />

            <div id="results" className="max-w-7xl mx-auto px-4 pt-12 md:pt-48 lg:pt-40 pb-20 font-sans">
                <div className="flex flex-col md:flex-row justify-between items-center md:items-end mb-12 gap-4">
                    <div className="text-center md:text-left">
                        <div className="flex items-center gap-4 justify-center md:justify-start">
                            <h2 className="text-4xl font-display font-black text-[#1A2A3A]">Biens à la Une</h2>
                            {isFilterActive && (
                                <button
                                    onClick={resetFilters}
                                    className="flex items-center gap-2 px-3 py-1 rounded-full bg-red-50 text-red-500 text-xs font-bold uppercase tracking-wider hover:bg-red-100 transition-colors"
                                    title="Effacer tous les filtres"
                                >
                                    <X size={14} /> <span>Effacer filtre</span>
                                </button>
                            )}
                        </div>
                        <div className="w-24 h-1.5 bg-[#EA723D] mt-3 rounded-full mx-auto md:mx-0"></div>
                    </div>
                    {/* Sélecteur de vue (Grille / Carte) */}
                    <div className="flex bg-gray-100 p-1.5 rounded-2xl items-center shadow-inner border border-gray-200 mt-4 md:mt-0 relative z-30">
                        <button
                            onClick={() => setViewMode('grid')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
                                viewMode === 'grid'
                                    ? 'bg-[#2C353D] text-white shadow-md'
                                    : 'text-gray-500 hover:text-[#2C353D]'
                            }`}
                        >
                            <LayoutGrid size={14} />
                            <span>Vue Grille</span>
                        </button>
                        <button
                            onClick={() => setViewMode('map')}
                            className={`flex items-center gap-2 px-5 py-2.5 rounded-xl text-xs font-black uppercase tracking-wider transition-all duration-300 ${
                                viewMode === 'map'
                                    ? 'bg-[#2C353D] text-white shadow-md'
                                    : 'text-gray-500 hover:text-[#2C353D]'
                            }`}
                        >
                            <MapPin size={14} />
                            <span>Recherche Carte</span>
                        </button>
                    </div>
                </div>

                {loading ? (
                    <div className="text-center py-20 flex flex-col items-center">
                        <div className="w-16 h-16 border-4 border-[#EA723D] border-t-transparent rounded-full animate-spin mb-4"></div>
                        <p className="text-gray-400 font-bold uppercase tracking-widest text-xs">Chargement en cours...</p>
                    </div>
                ) : viewMode === 'map' ? (
                    <MapSearch properties={mapProperties} />
                ) : (
                    <motion.div
                        variants={containerVariants}
                        initial="hidden"
                        animate="show"
                        className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
                    >
                        <AnimatePresence mode="wait">
                            {displayedProperties.map(property => (
                                <PropertyCard
                                    key={property.id}
                                    property={property}
                                    onClick={() => navigate(`/property/${property.id}`)}
                                />
                            ))}
                        </AnimatePresence>
                    </motion.div>
                )}

                {!loading && viewMode === 'grid' && (
                    <div className="mt-16 flex justify-center">
                        {!isFilterActive && filteredProperties.length > 6 && (
                            <Button
                                onClick={() => navigate('/annonces')}
                                variant="outline"
                                className="px-10 py-4 text-base rounded-xl border-2 border-[#2C353D] text-[#2C353D] hover:bg-[#2C353D] hover:text-white transition-all font-bold shadow-lg shadow-gray-900/5 group"
                            >
                                Voir toutes les annonces
                                <RefreshCcw size={18} className="ml-2 group-hover:rotate-180 transition-transform duration-700" />
                            </Button>
                        )}
                        {isFilterActive && filteredProperties.length > 6 && !showFilteredResults && (
                            <Button
                                onClick={() => setShowFilteredResults(true)}
                                variant="primary"
                                className="px-10 py-4 text-base rounded-xl font-bold shadow-xl shadow-blue-900/20"
                            >
                                Afficher les {filteredProperties.length} biens correspondants
                            </Button>
                        )}
                    </div>
                )}
            </div>

            <div className="bg-[#F4F7FA] py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="text-center mb-16">
                        <span className="text-[#EA723D] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">L'expérience Avantis</span>
                        <h2 className="text-3xl md:text-5xl font-display font-black text-[#1A2A3A]">Vos Avantages Agence</h2>
                    </div>
                    <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
                        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center">
                            <div className="w-full h-40 rounded-xl overflow-hidden mb-6">
                                <img src={realEstatePhotosImg} alt="Photos et visite virtuelle immobilière" className="w-full h-full object-cover" />
                            </div>
                            <h3 className="font-display font-bold text-lg text-[#1A2A3A] mb-3">Photos &amp; Visite Virtuelle</h3>
                            <p className="text-gray-500 text-sm leading-relaxed">Shooting HDR et <span className="font-bold text-[#EA723D]">visite immersive</span> pour sublimer votre bien dès le premier regard.</p>
                        </motion.div>
                        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center">
                            <div className="w-full h-40 rounded-xl overflow-hidden mb-6">
                                <img src={vanPhoto} alt="Camion de déménagement Avantis Immobilier" className="w-full h-full object-cover" />
                            </div>
                            <h3 className="font-display font-bold text-lg text-[#1A2A3A] mb-3">Camion de Déménagement</h3>
                            <p className="text-gray-500 text-sm leading-relaxed">Un véhicule utilitaire mis <span className="font-bold text-[#EA723D]">gratuitement</span> à votre disposition pour votre emménagement.</p>
                        </motion.div>
                        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center">
                            <div className="w-full h-40 rounded-xl overflow-hidden mb-6">
                                <img src={homeStagingPhoto} alt="Home Staging 3D Avantis Immobilier" className="w-full h-full object-cover" />
                            </div>
                            <h3 className="font-display font-bold text-lg text-[#1A2A3A] mb-3">Home Staging 3D</h3>
                            <p className="text-gray-500 text-sm leading-relaxed">Rénovation virtuelle et projections 3D pour révéler tout le <span className="font-bold text-[#EA723D]">potentiel</span> de vos espaces.</p>
                        </motion.div>
                        <motion.div whileHover={{ y: -5 }} className="bg-white p-8 rounded-2xl shadow-sm border border-gray-100 flex flex-col items-center text-center">
                            <div className="w-full h-40 rounded-xl overflow-hidden mb-6">
                                <img src={agencyConsultationImg} alt="Accompagnement expert Avantis Immobilier" className="w-full h-full object-cover" />
                            </div>
                            <h3 className="font-display font-bold text-lg text-[#1A2A3A] mb-3">Accompagnement Expert</h3>
                            <p className="text-gray-500 text-sm leading-relaxed">Une équipe de professionnels pour vous <span className="font-bold text-[#EA723D]">sécuriser</span> à chaque étape de votre vente.</p>
                        </motion.div>
                    </div>
                </div>
            </div>

            <Testimonials />

            <div className="bg-white py-24 border-t border-gray-100 relative overflow-hidden font-sans">
                <div className="max-w-6xl mx-auto px-4 relative z-10">
                    <div className="max-w-3xl mx-auto text-center">
                        <div className="text-center">
                            <span className="text-[#EA723D] font-bold text-xs uppercase tracking-[0.2em] mb-4 block">Une expertise luxembourgeoise</span>
                            <h2 className="text-4xl md:text-5xl font-display font-bold text-[#1A2A3A] mb-6 leading-tight">
                                Avantis Immobilier.
                            </h2>
                            <h3 className="text-xl text-gray-400 font-light mb-8">Votre agence de référence au Luxembourg.</h3>

                            <div className="text-gray-500 leading-relaxed mb-8 text-base font-normal space-y-4 text-justify">
                                <p>
                                    Spécialiste de l'immobilier luxembourgeois, <strong>Avantis Immobilier</strong> vous accompagne dans tous vos projets immobiliers au Grand-Duché. Que vous cherchiez à acheter, vendre ou louer, notre équipe met son expertise du marché local à votre service.
                                </p>
                                <p>
                                    Notre connaissance approfondie du marché immobilier luxembourgeois, de ses spécificités fiscales et juridiques, fait de nous le partenaire idéal pour réussir votre transaction immobilière au Luxembourg.
                                </p>
                                <p className="text-xl font-display font-medium text-[#EA723D] italic text-center py-4 leading-relaxed">
                                    "L'immobilier luxembourgeois est notre passion. Nous mettons tout notre savoir-faire au service de vos projets."
                                </p>
                                <p className="font-bold text-[#1A2A3A] italic pt-2 text-center md:text-left">
                                    Besoin d'un conseil expert pour votre projet au Luxembourg ?
                                </p>
                            </div>

                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <Button variant="secondary" onClick={() => navigate('/contact')} className="shadow-xl shadow-blue-900/10 px-8 py-4 text-base rounded-xl">
                                    Contacter l'agence
                                </Button>
                                <Button variant="primary" onClick={() => navigate('/estimation')} className="px-8 py-4 text-base rounded-xl">
                                    Estimer mon bien
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="bg-[#F7F7F7] py-16 border-t border-gray-100">
                <div className="max-w-4xl mx-auto px-4 text-center md:text-left">
                    <h2 className="text-xl font-bold text-[#1A2A3A] mb-6">Expertise Immobilière au Luxembourg</h2>
                    <div className="text-gray-400 text-xs leading-relaxed space-y-4 text-justify">
                        <p>
                            <strong>Marché Immobilier Luxembourg :</strong> Immobilier Luxembourg-Ville, Vente maison Esch-sur-Alzette, Appartements à Dudelange, Achat Differdange, Estimation Petange, Biens Strassen, Bertrange, Hesperange.
                        </p>
                        <p>
                            <strong>Nos services :</strong> Estimation immobilière au Luxembourg, Vente de maisons et appartements, Gestion locative Luxembourg, Immobilier neuf et ancien, Expertise marché luxembourgeois.
                        </p>
                        <p>
                            Avantis Immobilier.
                        </p>
                    </div>
                </div>
            </div>
        </>
    );
};

export default Home;
